import { useEffect, useState } from "react";
import { Store, Copy, Unlink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const copy = (v, label) => navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`));

export const ShopifyConnect = () => {
  const [s, setS] = useState(null);
  const [domain, setDomain] = useState("");
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/brand/shopify").then((r) => setS(r.data)).catch(() => setS({ connected: false }));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    if (!domain.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/brand/shopify/connect", { shop_domain: domain });
      setCreds(data);
      await load();
      toast.success("Webhook created — paste it into Shopify");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => {
    await api.delete("/brand/shopify");
    setCreds(null);
    await load();
    toast.success("Shopify disconnected");
  };

  if (!s) return null;
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="shopify-connect">
      <div className="flex items-center gap-2">
        <Store className="h-4 w-4 text-nexus-blue" />
        <div className="text-xs uppercase tracking-widest text-zinc-500">Shopify sales attribution</div>
      </div>
      <p className="text-sm text-zinc-400">Connect your store so creator sales are counted automatically for performance deals. No app install — one webhook in Shopify Admin.</p>
      {!s.connected ? (
        <div className="flex gap-2">
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="your-store.myshopify.com" className="bg-white/[0.02] border-white/10 text-white" data-testid="shopify-domain-input" />
          <Button onClick={connect} disabled={busy || !domain.trim()} className="nexus-gradient text-white rounded-full border-0 shrink-0" data-testid="shopify-connect-btn">Connect</Button>
        </div>
      ) : (
        <div className="space-y-3 text-sm" data-testid="shopify-connected">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold">{s.shop_domain}</span>
            <span className="text-xs text-zinc-500">{s.orders_received} orders received · {s.attributed} attributed</span>
          </div>
          <div className="p-3 rounded-lg bg-black/30 border border-white/5 space-y-2 text-xs">
            <div className="text-zinc-400">In Shopify Admin → Settings → Notifications → Webhooks → <b className="text-white">Create webhook</b>: Event <b className="text-white">Order creation</b>, Format JSON, URL below.</div>
            <div className="flex items-center gap-2"><code className="flex-1 truncate text-nexus-blue" data-testid="shopify-webhook-url">{s.webhook_url}</code><button onClick={() => copy(s.webhook_url, "Webhook URL")} className="text-zinc-400 hover:text-white" data-testid="copy-webhook-url"><Copy className="h-3.5 w-3.5" /></button></div>
            {creds?.secret && (
              <div className="flex items-center gap-2"><span className="text-zinc-500 shrink-0">Signing secret (shown once):</span><code className="flex-1 truncate text-nexus-pink" data-testid="shopify-secret">{creds.secret}</code><button onClick={() => copy(creds.secret, "Secret")} className="text-zinc-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button></div>
            )}
            <div className="text-zinc-500">Also create a discount code equal to each creator's Nexus code (shown in the Deal Room) so checkout sales attribute even without the link.</div>
          </div>
          <Button variant="outline" size="sm" onClick={disconnect} className="rounded-full border-white/15 text-white hover:bg-white/10 hover:text-white" data-testid="shopify-disconnect-btn"><Unlink className="h-3.5 w-3.5 mr-1.5" /> Disconnect</Button>
        </div>
      )}
    </div>
  );
};
